import React, { useState } from 'react';
import { API_CONFIG } from '../../api/config';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Button } from '@/components/atoms/Button';
import Navbar from '@/components/organisms/Navbar';
import ContactForm from '@/components/organisms/ContactForm';
import { motion, AnimatePresence } from 'motion/react';

// Animation variants
const formFieldVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.15 + i * 0.08,
            duration: 0.4,
            ease: [0.25, 0.1, 0.25, 1] as const,
        },
    }),
};

const successVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: "spring" as const,
            damping: 20,
            stiffness: 300,
        },
    },
};

const BetaPartnerPage: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        role: '',
        industry: 'Festivals',
        message: '',
        consent: false
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showContactForm, setShowContactForm] = useState(false);

    const industries = [
        'Festivals',
        'Theatre',
        'Corporate',
        'Conferences',
        'Concerts',
        'Exhibitions'
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.company || !formData.consent) {
            setError('Please fill in all required fields and accept the privacy policy.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const emailData = {
                fromAddress: formData.email,
                message: `
Beta Partner Registration:
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Role: ${formData.role || 'Not specified'}
Industry: ${formData.industry}
Message: ${formData.message || 'No message provided'}
                `,
                subject: `unimo Beta Partner: ${formData.name} from ${formData.company}`,
                name: formData.name,
                company: formData.company,
                role: formData.role,
                industry: formData.industry,
                formType: 'beta-partner'
            };

            const response = await fetch(API_CONFIG.BETA_PARTNER_EMAIL_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(emailData),
            });

            const data = await response.json();

            if (data.success) {
                setIsSubmitted(true);
            } else {
                throw new Error(data.message || 'Unknown error occurred');
            }
        } catch (err) {
            console.error('Error submitting form:', err);
            setError('Something went wrong. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar onContactClick={() => setShowContactForm(true)} />

            {/* Hero Section */}
            <section className="bg-white border-b border-gray-200">
                <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-unimo-turquoise/10 text-unimo-turquoise rounded-full text-sm font-medium mb-6"
                        >
                            <span className="w-2 h-2 bg-unimo-turquoise rounded-full"></span>
                            Beta Programme
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-4xl md:text-5xl font-medium text-gray-900 mb-6"
                        >
                            Become a Beta Partner
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="text-xl text-gray-600"
                        >
                            Join us in revolutionising event monitoring technology. We're looking for partners to test unimo in real-world environments throughout 2026.
                        </motion.p>
                    </div>
                </div>
            </section>

            {/* Form Section */}
            <section className="py-16 md:py-20">
                <div className="max-w-2xl mx-auto px-4">
                    <AnimatePresence mode="wait">
                        {!isSubmitted ? (
                            <motion.div
                                key="form"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm"
                            >
                                <h2 className="text-2xl font-medium text-gray-900 mb-1">Register Your Interest</h2>
                                <p className="text-gray-500 text-sm mb-6">Tell us about your organisation and monitoring needs</p>

                                {/* Error Message */}
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10, height: 0 }}
                                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                                            exit={{ opacity: 0, y: -10, height: 0 }}
                                            className="flex items-start gap-3 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-100 mb-6"
                                        >
                                            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>{error}</span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {/* Name */}
                                        <motion.div
                                            custom={0}
                                            variants={formFieldVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Name *
                                            </label>
                                            <Input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                placeholder="Your name"
                                                required
                                                disabled={isSubmitting}
                                            />
                                        </motion.div>

                                        {/* Email */}
                                        <motion.div
                                            custom={1}
                                            variants={formFieldVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Email *
                                            </label>
                                            <Input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                placeholder="your.email@company.com"
                                                required
                                                disabled={isSubmitting}
                                            />
                                        </motion.div>

                                        {/* Company */}
                                        <motion.div
                                            custom={2}
                                            variants={formFieldVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Company/Organisation *
                                            </label>
                                            <Input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                placeholder="Your company name"
                                                required
                                                disabled={isSubmitting}
                                            />
                                        </motion.div>

                                        {/* Role */}
                                        <motion.div
                                            custom={3}
                                            variants={formFieldVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Your Role
                                            </label>
                                            <Input
                                                type="text"
                                                id="role"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                placeholder="e.g. Production Manager"
                                                disabled={isSubmitting}
                                            />
                                        </motion.div>

                                        {/* Industry */}
                                        <motion.div
                                            custom={4}
                                            variants={formFieldVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Industry
                                            </label>
                                            <Select
                                                id="industry"
                                                name="industry"
                                                value={formData.industry}
                                                onChange={handleChange}
                                                className="w-full h-11 px-4 border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                                                disabled={isSubmitting}
                                            >
                                                {industries.map(industry => (
                                                    <option key={industry} value={industry}>
                                                        {industry}
                                                    </option>
                                                ))}
                                            </Select>
                                        </motion.div>
                                    </div>

                                    {/* Message */}
                                    <motion.div
                                        custom={5}
                                        variants={formFieldVariants}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                                            What are you most interested in monitoring?
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={4}
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow text-sm placeholder-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                                            placeholder="Tell us about your monitoring needs..."
                                            disabled={isSubmitting}
                                        />
                                    </motion.div>

                                    {/* Consent */}
                                    <motion.div
                                        custom={6}
                                        variants={formFieldVariants}
                                        initial="hidden"
                                        animate="visible"
                                        className="flex items-start gap-3"
                                    >
                                        <input
                                            id="consent"
                                            name="consent"
                                            type="checkbox"
                                            checked={formData.consent}
                                            onChange={handleCheckboxChange}
                                            className="mt-0.5 w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                                            required
                                            disabled={isSubmitting}
                                        />
                                        <label htmlFor="consent" className="text-sm text-gray-600">
                                            I consent to being contacted about the unimo beta programme. We'll only use your information to discuss beta partnering opportunities with a real human, not for marketing. *
                                        </label>
                                    </motion.div>

                                    {/* Submit */}
                                    <motion.div
                                        custom={7}
                                        variants={formFieldVariants}
                                        initial="hidden"
                                        animate="visible"
                                        className="pt-2"
                                    >
                                        <Button
                                            type="submit"
                                            className="w-full h-11 bg-gray-900 text-white hover:bg-gray-800 rounded-lg font-medium transition-all hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Processing...
                                                </span>
                                            ) : (
                                                'Register Interest'
                                            )}
                                        </Button>
                                    </motion.div>
                                </form>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="success"
                                variants={successVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white border border-gray-200 rounded-xl p-8 md:p-12 shadow-sm text-center"
                            >
                                <motion.div
                                    className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring" as const, damping: 15, stiffness: 300, delay: 0.1 }}
                                >
                                    <motion.svg
                                        className="w-8 h-8 text-emerald-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </motion.svg>
                                </motion.div>
                                <h3 className="text-2xl font-medium text-gray-900 mb-2">Thank You!</h3>
                                <p className="text-gray-600 mb-2">
                                    Your beta partner application has been submitted successfully.
                                </p>
                                <p className="text-gray-500 text-sm mb-8">
                                    We'll be in touch soon to discuss how we can work together on the unimo beta programme.
                                    If you have any immediate questions, please email us at{' '}
                                    <a href="mailto:david@crossmoore.io" className="text-gray-900 hover:underline font-medium">
                                        david@crossmoore.io
                                    </a>.
                                </p>
                                <a
                                    href="/"
                                    className="inline-block px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
                                >
                                    Return to Home
                                </a>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* CTA Section */}
            {!isSubmitted && (
                <section className="py-16 md:py-20 bg-gray-900">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-3xl font-medium text-white mb-4"
                        >
                            Shape the Future of Event Monitoring
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-gray-400 mb-8 text-lg"
                        >
                            Beta partners get early access, direct input into product development, and preferential pricing at launch.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <button
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                className="px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Register Now
                            </button>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="bg-gray-900 border-t border-gray-800 py-8">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-white font-medium">unimo</span>
                            <span className="text-gray-500 text-sm">&copy; 2026</span>
                        </div>
                        <div className="flex gap-6 text-sm">
                            <a href="/" className="text-gray-400 hover:text-white">Home</a>
                            <a href="/industries/events" className="text-gray-400 hover:text-white">Events</a>
                            <a href="/industries/construction" className="text-gray-400 hover:text-white">Construction</a>
                            <a href="/industries/tv-film" className="text-gray-400 hover:text-white">TV & Film</a>
                            <button onClick={() => setShowContactForm(true)} className="text-gray-400 hover:text-white">Contact</button>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Contact Form Modal */}
            {showContactForm && (
                <ContactForm onClose={() => setShowContactForm(false)} />
            )}
        </div>
    );
};

export default BetaPartnerPage;
